import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, Check, Eye, EyeOff, ExternalLink, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Use environment variable for API calls, but detect actual URL for WordPress config
const API_URL = process.env.REACT_APP_BACKEND_URL;
const PLUGIN_VERSION = "7.0.0";

// Get the actual API URL to display to users (handles both preview and production)
const getDisplayApiUrl = () => {
  // If we're on a deployed domain, use that domain
  const currentHost = window.location.hostname;
  const currentProtocol = window.location.protocol;
  
  // Check if we're on a production/deployed domain (not localhost)
  if (currentHost !== 'localhost' && currentHost !== '127.0.0.1') {
    // Return the current origin as the API URL
    return `${currentProtocol}//${currentHost}`;
  }
  
  // Fallback to environment variable for local development
  return API_URL;
};

const WordPressIntegration = () => {
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [displayApiUrl, setDisplayApiUrl] = useState('');

  useEffect(() => {
    fetchApiKey();
    setDisplayApiUrl(getDisplayApiUrl());
  }, []);

  const fetchApiKey = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/clinics/api-key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKey(response.data.api_key);
    } catch (error) {
      console.error('Error fetching API key:', error);
      toast.error('Failed to load API key');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`${label} copied to clipboard`);
    setTimeout(() => setCopied(false), 2000);
  };

  const regenerateApiKey = async () => {
    if (!window.confirm('Are you sure you want to regenerate your API key? Your existing WordPress integration will stop working until you update it.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/clinics/regenerate-api-key`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKey(response.data.api_key);
      toast.success('API key regenerated successfully');
    } catch (error) {
      toast.error('Failed to regenerate API key');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">WordPress Integration</h2>
          <p className="text-gray-600">Configure your WordPress plugin to display membership plans on your website</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="bg-gradient-to-r from-cyan-600 to-cyan-500 text-white px-6 py-3 rounded-lg font-bold text-lg shadow-lg">
            Plugin v{PLUGIN_VERSION}
          </div>
          <span className="text-xs text-gray-500">Latest Version</span>
        </div>
      </div>

      {/* Auto-Update Notice */}
      <Card className="border-green-200 bg-green-50">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <RefreshCw className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-sm font-semibold text-green-800">One-Click Updates Enabled</p>
              <p className="text-xs text-green-700">Your WordPress plugin will automatically detect new versions. Update with one click from WordPress Plugins page.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step 1: Download Plugin */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold">1</div>
              <CardTitle>Download WordPress Plugin</CardTitle>
            </div>
            <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-xs font-semibold">
              v{PLUGIN_VERSION} - NEW!
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-2 border-cyan-200">
            <p className="text-sm font-semibold text-cyan-900 mb-2">What is New in v{PLUGIN_VERSION}:</p>
            <ul className="text-sm text-cyan-800 space-y-1 list-disc list-inside">
              <li><strong>Plugin Renamed to MyMedMember</strong></li>
              <li><strong>Full Branding Customization</strong> - Colors, border radius, and more</li>
              <li><strong>CSS Variables</strong> - Real-time style updates</li>
              <li><strong>Live Preview</strong> - See changes instantly in admin</li>
              <li><strong>Enhanced Button Styling</strong> - Gradients and hover effects</li>
            </ul>
          </div>
          <p className="text-gray-600">Download and install the MyMedMember WordPress plugin on your website.</p>
          <a href="https://mymedmember.com/api/download/wordpress-plugin" download>
            <Button className="bg-teal-600 hover:bg-teal-700 text-lg py-6 px-8">
              <ExternalLink className="h-5 w-5 mr-2" />
              Download Plugin v{PLUGIN_VERSION} (ZIP)
            </Button>
          </a>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700 font-medium mb-2">Installation Steps:</p>
            <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
              <li>Download the plugin ZIP file</li>
              <li>Go to WordPress Admin → Plugins → Add New → Upload Plugin</li>
              <li>Choose the ZIP file and click Install Now</li>
              <li>Activate the plugin</li>
            </ol>
          </div>
          <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm font-semibold text-amber-800 mb-1">Already have the plugin installed?</p>
            <p className="text-sm text-amber-700">Go to WordPress Admin → Plugins → Check for updates. If v{PLUGIN_VERSION} is available, click Update Now to upgrade with one click!</p>
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Configure Plugin Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold">2</div>
            <CardTitle>Configure Plugin Settings</CardTitle>
          </div>
          <CardDescription>Copy these credentials to your WordPress plugin settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* API URL */}
          <div className="space-y-2">
            <Label>API URL</Label>
            <div className="flex gap-2">
              <Input 
                value={displayApiUrl} 
                readOnly 
                className="font-mono text-sm bg-green-50 border-green-200"
              />
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => copyToClipboard(displayApiUrl, 'API URL')}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* API Key */}
          <div className="space-y-2">
            <Label>API Key</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input 
                  value={apiKey} 
                  type={showApiKey ? 'text' : 'password'}
                  readOnly 
                  className="font-mono text-sm pr-10"
                />
                <button
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => copyToClipboard(apiKey, 'API Key')}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-sm text-gray-500">Keep this key secure. Anyone with this key can access your plans.</p>
          </div>

          <div className="pt-2">
            <Button 
              variant="outline" 
              onClick={regenerateApiKey}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              Regenerate API Key
            </Button>
          </div>

          {/* WordPress Configuration Instructions */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700 font-medium mb-2">In WordPress Admin:</p>
            <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
              <li>Go to <strong>MyMedMember → Settings</strong></li>
              <li>Paste the <strong>API URL</strong> in the API URL field</li>
              <li>Paste the <strong>API Key</strong> in the API Key field</li>
              <li>Click <strong>Save Settings</strong></li>
              <li>You should see "✓ Connected" status</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Step 3: Add Shortcode */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold">3</div>
            <CardTitle>Add Membership Plans to Your Site</CardTitle>
          </div>
          <CardDescription>Use shortcodes to display membership plans on any page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            {/* Basic Shortcode */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Basic Display (3 columns)</p>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => copyToClipboard('[medmember_plans]', 'Shortcode')}
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <code className="text-sm text-teal-600 font-mono">[medmember_plans]</code>
            </div>

            {/* 2 Column Layout */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">2 Column Layout</p>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => copyToClipboard('[medmember_plans columns="2"]', 'Shortcode')}
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <code className="text-sm text-teal-600 font-mono">[medmember_plans columns="2"]</code>
            </div>

            {/* 4 Column Layout */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">4 Column Layout</p>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => copyToClipboard('[medmember_plans columns="4"]', 'Shortcode')}
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <code className="text-sm text-teal-600 font-mono">[medmember_plans columns="4"]</code>
            </div>
          </div>

          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800 font-medium mb-2">💡 Pro Tip:</p>
            <p className="text-sm text-blue-700">Only <strong>Active</strong> plans will be displayed on your website. Use the "Activate/Deactivate" buttons in Membership Plans to control which plans are visible to customers.</p>
          </div>
        </CardContent>
      </Card>

      {/* Step 4: Test Integration */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold">4</div>
            <CardTitle>Test Your Integration</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700 font-medium mb-2">Testing Checklist:</p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-teal-600 font-bold">→</span>
                <span>Visit your WordPress page with the shortcode</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-600 font-bold">→</span>
                <span>Verify that only Active plans are showing</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-600 font-bold">→</span>
                <span>Click "Subscribe Now" on a plan</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-600 font-bold">→</span>
                <span>Fill out the subscription form</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-600 font-bold">→</span>
                <span>Check email for login credentials</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-600 font-bold">→</span>
                <span>Verify patient appears in Admin Dashboard</span>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Help & Support */}
      <Card className="border-teal-200 bg-teal-50">
        <CardHeader>
          <CardTitle className="text-teal-900">Need Help?</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-teal-800 mb-4">
            If you encounter any issues during setup, our support team is here to help.
          </p>
          <div className="space-y-2 text-sm text-teal-700">
            <p><strong>Common Issues:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Plugin not connecting? Double-check API URL and API Key</li>
              <li>No plans showing? Ensure you have Active plans in Membership Plans</li>
              <li>Emails not sending? Check your WordPress email configuration</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WordPressIntegration;
